import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MessageCircle, MoreVertical, MapPin } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import "./GuiaIa.css";

type ParceiroDestaque = {
  id: string;
  nome: string;
  lat: number;
  lng: number;
};

interface Message {
  role: "user" | "assistant";
  content: string;
  parceiros?: ParceiroDestaque[];
}

function parseStoredAssistantContent(raw: string): {
  text: string;
  parceiros?: ParceiroDestaque[];
} {
  try {
    const o = JSON.parse(raw) as {
      v?: number;
      text?: string;
      parceiros?: ParceiroDestaque[];
    };
    if (o?.v === 1 && typeof o.text === "string") {
      const parceiros = Array.isArray(o.parceiros)
        ? o.parceiros.filter(
            (p) =>
              p &&
              typeof p.id === "string" &&
              typeof p.nome === "string" &&
              typeof p.lat === "number" &&
              typeof p.lng === "number",
          )
        : undefined;
      return { text: o.text, parceiros };
    }
  } catch {
    /* texto puro */
  }
  return { text: raw };
}

export default function GuiaIa() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const userId = user?.id ?? "guest";

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    const { data, error } = await supabase
      .from("conversations")
      .select("role, content")
      .eq("user_id", userId)
      .order("created_at", { ascending: true })
      .limit(80);

    if (error) {
      console.error("[GuiaIa] histórico:", error);
      setMessages([]);
    } else {
      const rows = data ?? [];
      setMessages(
        rows.map((r) => {
          const role = r.role as "user" | "assistant";
          if (role === "assistant") {
            const p = parseStoredAssistantContent(String(r.content ?? ""));
            return {
              role: "assistant",
              content: p.text,
              parceiros: p.parceiros,
            };
          }
          return { role: "user", content: String(r.content ?? "") };
        }),
      );
    }
    setHistoryLoading(false);
  }, [userId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [menuOpen]);

  async function clearConversation() {
    setMenuOpen(false);
    if (!user?.id || user.id === "guest") {
      setMessages([]);
      toast.success("Conversa limpa neste aparelho.");
      return;
    }
    if (!window.confirm("Apagar todo o histórico com o assistente?")) return;

    try {
      const { data, error } = await supabase.functions.invoke("guia-turistico", {
        body: {
          clear_history: true,
          user_id: user.id,
          user_email: user.email,
        },
      });
      if (error) throw error;
      if (data && typeof data === "object" && "error" in data) {
        throw new Error(String((data as { error: string }).error));
      }
      setMessages([]);
      toast.success("Conversa apagada.");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Não foi possível apagar.";
      toast.error(msg);
    }
  }

  function openMapRoute(p: ParceiroDestaque) {
    navigate(`/map?focus=${encodeURIComponent(p.id)}&route=1`);
  }

  async function sendMessage() {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("guia-turistico", {
        body: { message: userMessage, user_id: userId },
      });

      if (error) throw error;

      const payload = data as {
        reply?: string;
        parceiros_destaque?: ParceiroDestaque[];
        error?: string;
      } | null;

      if (payload?.error) throw new Error(payload.error);

      const reply =
        typeof payload?.reply === "string"
          ? payload.reply
          : "Não recebi uma resposta válida. Tente de novo em instantes.";

      const parceiros = Array.isArray(payload?.parceiros_destaque)
        ? payload.parceiros_destaque.filter(
            (p) =>
              p &&
              typeof p.id === "string" &&
              typeof p.nome === "string" &&
              typeof p.lat === "number" &&
              typeof p.lng === "number",
          )
        : undefined;

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: reply, parceiros },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Erro ao conectar com o guia. Tente novamente.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  }

  return (
    <div className="guia-ia-page">
      <header className="guia-ia-top">
        <button
          type="button"
          className="guia-ia-back"
          onClick={() => navigate(-1)}
          aria-label="Voltar"
        >
          <ArrowLeft size={22} />
        </button>
        <div className="guia-ia-heading">
          <h1>Assistente virtual</h1>
          <p>Pergunte sobre destinos, dicas e roteiros em Ubatuba e região</p>
        </div>
        <div className="guia-ia-menu-wrap" ref={menuRef}>
          <button
            type="button"
            className="guia-ia-menu-btn"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-label="Opções da conversa"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
          >
            <MoreVertical size={22} />
          </button>
          {menuOpen && (
            <div className="guia-ia-dropdown" role="menu">
              <button
                type="button"
                role="menuitem"
                className="guia-ia-dropdown-item guia-ia-dropdown-item--danger"
                onClick={() => void clearConversation()}
              >
                Apagar conversa
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="guia-ia-messages">
        {historyLoading && (
          <div className="guia-ia-loading-hint" aria-live="polite">
            Carregando conversa…
          </div>
        )}

        {!historyLoading && messages.length === 0 && !loading && (
          <div className="guia-ia-empty">
            <span className="guia-ia-empty-icon" aria-hidden>
              <MessageCircle size={30} strokeWidth={2} />
            </span>
            <p>Envie uma mensagem para falar com o guia.</p>
            <small>Praias, parceiros do app e roteiros em Ubatuba</small>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`guia-ia-row ${msg.role === "user" ? "guia-ia-row--user" : "guia-ia-row--assistant"}`}
          >
            <div
              className={`guia-ia-bubble ${msg.role === "user" ? "guia-ia-bubble--user" : "guia-ia-bubble--assistant"}`}
            >
              <div className="guia-ia-bubble-text">{msg.content}</div>
              {msg.role === "assistant" && msg.parceiros && msg.parceiros.length > 0 && (
                <div className="guia-ia-map-actions">
                  {msg.parceiros.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="guia-ia-map-btn"
                      onClick={() => openMapRoute(p)}
                    >
                      <MapPin size={16} aria-hidden />
                      Rota no mapa: {p.nome}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="guia-ia-row guia-ia-row--assistant">
            <div className="guia-ia-bubble guia-ia-bubble--assistant">
              <div className="guia-ia-typing" aria-hidden>
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <footer className="guia-ia-composer">
        <div className="guia-ia-form">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Digite sua pergunta..."
            rows={1}
            className="guia-ia-input"
            aria-label="Mensagem para o assistente"
            disabled={historyLoading}
          />
          <button
            type="button"
            onClick={() => void sendMessage()}
            disabled={loading || !input.trim() || historyLoading}
            className="guia-ia-send"
          >
            Enviar
          </button>
        </div>
      </footer>
    </div>
  );
}
